import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useDirty, useUnsavedGuard } from '../../components/unsaved';
import {
  ResolvedEndpoints, createProviderApi, deleteProviderApi, getProviderApi, runProviderChecksApi,
  sendProviderTestLink, updateProviderApi,
} from '../../api/admin';
import type { ConnectionChecks, IdentityProvider, ProviderProtocol } from '../../shared/types';
import { ChecksList, CopyUri, DomainChips, StatusPill, ago } from './SsoShared';
import { ActionBar } from '../../components/fs';
import { PHONE, useMediaQuery } from '../../shared/media';

/**
 * Provider setup and edit — one page, no wizard, for both.
 *
 * The right-hand rail is the point of the screen: everything about an IdP that
 * can be proved WITHOUT a login is proved here, because the person configuring
 * a customer's provider has no account at it. The one remaining question — can
 * a real human complete the round trip — answers itself on the first sign-in.
 */
export default function ProviderEditor() {
  const { id = 'new' } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [provider, setProvider] = useState<IdentityProvider | null>(null);
  const [name, setName] = useState('');
  const [protocol, setProtocol] = useState<ProviderProtocol>('oidc');
  const [cfg, setCfg] = useState<Record<string, any>>({});
  const [domains, setDomains] = useState<string[]>([]);
  const [claimNames, setClaimNames] = useState<string[]>([]);
  const [newClaim, setNewClaim] = useState('');

  const [checks, setChecks] = useState<ConnectionChecks | null>(null);
  const [resolved, setResolved] = useState<ResolvedEndpoints | null>(null);
  const [checking, setChecking] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showTestLink, setShowTestLink] = useState(false);
  const [testLinkEmail, setTestLinkEmail] = useState('');
  const [testLinkMsg, setTestLinkMsg] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const toast = useToast();
  /**
   * On a phone the checks rail comes first and collapses, and Save moves
   * into a sticky bar (§4G G4). Arriving with `?check=1` opens it, because
   * that link exists to show a result.
   */
  const phone = useMediaQuery(PHONE);
  const [checksOpen, setChecksOpen] = useState(params.get('check') === '1');

  /**
   * A provider is a page of typing — issuer, client id, secret, domains — and
   * it is reached from a list one click away, so leaving half-configured is
   * the easy mistake to make. A new provider has nothing stored yet, so its
   * baseline is the empty form and the first character counts as a change.
   */
  const snapshot = useMemo(
    () => ({ name, protocol, cfg, domains, claimNames }),
    [name, protocol, cfg, domains, claimNames]
  );
  const { dirty, markSaved } = useDirty(snapshot, isNew || !!provider);
  const guard = useUnsavedGuard({
    dirty, busy: saving,
    what: isNew ? 'This provider has not been created yet, and what you typed' : 'Your changes to this provider',
    ...(name.trim() ? { onSave: () => save(), saveLabel: isNew ? 'Create and leave' : 'Save and leave' } : {}),
  });

  useEffect(() => {
    if (isNew) return;
    getProviderApi(id)
      .then((r) => {
        const p = r.provider;
        setProvider(p);
        setName(p.name);
        setProtocol(p.protocol);
        setCfg({ ...p.config });
        setDomains(p.allowedDomains);
        setClaimNames(p.claimNames);
        setChecks(p.checks);
        setResolved(p.checks?.resolved || null);
        // ?check=1 is how "Test connection" arrives from the settings page.
        if (params.get('check') === '1') void runChecks(p.id);
      })
      .catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const body = () => ({
    name,
    protocol,
    config: cfg,
    allowedDomains: domains,
    claimNames,
  });

  async function save() {
    setSaving(true); setError('');
    try {
      if (isNew) {
        const r = await createProviderApi(body());
        markSaved();
        toast(`${r.provider.name} created.`);
        navigate(`/admin/settings/access/providers/${r.provider.id}`, { replace: true });
      } else {
        const r = await updateProviderApi(Number(id), body());
        setProvider(r.provider);
        // The secret is write-only; drop what was typed so the field goes back
        // to reading "stored" rather than holding a value we can never re-show.
        setCfg({ ...r.provider.config });
        markSaved();
        toast('Provider saved.');
      }
    } catch (e: any) {
      setError(e.message);
      throw e;   // so "Save and leave" stays put rather than losing the edit
    }
    finally { setSaving(false); }
  }

  async function runChecks(providerId?: number) {
    const target = providerId ?? Number(id);
    if (!target || Number.isNaN(target)) return;
    setChecking(true); setError('');
    try {
      const r = await runProviderChecksApi(target);
      setChecks(r.checks);
      setResolved(r.resolved);
      setProvider(r.provider);
      setClaimNames(r.provider.claimNames);
    } catch (e: any) { setError(e.message); }
    finally { setChecking(false); }
  }

  async function remove() {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setError('');
    try {
      await deleteProviderApi(Number(id));
      navigate('/admin/settings/access');
    } catch (e: any) { setError(e.message); setConfirmDelete(false); }
  }

  const manual = protocol === 'oidc_manual';
  const saml = protocol === 'saml';
  /** the six rows `ChecksList` draws, and how many of them are green */
  const CHECK_ROWS = ['discovery', 'jwks', 'flow', 'credentials', 'redirectUri', 'liveSignIn'] as const;
  const CHECK_COUNT = CHECK_ROWS.length;
  const passing = checks ? CHECK_ROWS.filter((k) => checks[k]?.status === 'pass').length : 0;
  const set = (k: string, v: unknown) => setCfg({ ...cfg, [k]: v });

  return (
    <div onClick={() => confirmDelete && setConfirmDelete(false)}>
      <Link to="/admin/settings/access" className="st-crumb">← Sign-in &amp; access</Link>
      <div className="st-titlerow">
        <h1>{name || (isNew ? 'New identity provider' : 'Identity provider')}</h1>
        {provider && <StatusPill status={provider.status} />}
        {provider?.usage?.teamSignIn && <span className="ad-pill sso">team sign-in</span>}
        {!!provider?.usage?.forms.length && (
          <span className="ad-pill simple">used by {provider.usage.forms.length} form{provider.usage.forms.length === 1 ? '' : 's'}</span>
        )}
      </div>

      {error && <div className="ad-err">{error}</div>}

      <div className="sso-cols pe-editor">
        <div className="main">
          {/* ---- Provider ---- */}
          <div className="fs-card pad st-pcard">
            <h2>Provider</h2>
            <div className="ad-field" style={{ maxWidth: 460 }}>
              <label>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="T-Mobile Federal" />
              <small style={{ color: 'var(--text-muted)' }}>Appears on the button: “Continue with {name || 'T-Mobile Federal'}”.</small>
            </div>
            <div className="ad-field" style={{ maxWidth: 460 }}>
              <label>Protocol</label>
              <select value={protocol} onChange={(e) => setProtocol(e.target.value as ProviderProtocol)}>
                <option value="oidc">OIDC — discovery URL</option>
                <option value="oidc_manual">OIDC — endpoints entered by hand</option>
                <option value="saml">SAML 2.0 — metadata XML</option>
              </select>
              {saml && (
                <small style={{ color: 'var(--text-muted)' }}>
                  Trust comes from the signature on each assertion, so there is no client secret to enter.
                </small>
              )}
            </div>
          </div>

          {/* ---- Connection ---- */}
          <div className="fs-card pad st-pcard">
            <h2>Connection</h2>
            {!manual && !saml && (
              <div className="ad-field">
                <label>Discovery URL</label>
                <input
                  value={cfg.discoveryUrl || ''}
                  placeholder="https://acme.okta.com/.well-known/openid-configuration"
                  onChange={(e) => set('discoveryUrl', e.target.value)}
                />
              </div>
            )}
            {manual && (
              <div className="fs-row-2" style={{ gap: 12 }}>
                <div className="ad-field"><label>Issuer</label><input value={cfg.issuer || ''} onChange={(e) => set('issuer', e.target.value)} /></div>
                <div className="ad-field"><label>Authorization endpoint</label><input value={cfg.authorizationEndpoint || ''} onChange={(e) => set('authorizationEndpoint', e.target.value)} /></div>
                <div className="ad-field"><label>Token endpoint</label><input value={cfg.tokenEndpoint || ''} onChange={(e) => set('tokenEndpoint', e.target.value)} /></div>
                <div className="ad-field"><label>JWKS URI</label><input value={cfg.jwksUri || ''} onChange={(e) => set('jwksUri', e.target.value)} /></div>
              </div>
            )}
            {saml && (
              <>
                <div className="ad-field">
                  <label>Metadata URL</label>
                  <input
                    value={cfg.metadataUrl || ''}
                    placeholder="https://idp.example.com/app/exk123/sso/saml/metadata"
                    onChange={(e) => set('metadataUrl', e.target.value)}
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    Re-read whenever the checks run, so a rotated signing certificate is picked up without anyone re-pasting it.
                  </small>
                </div>
                <div className="ad-field">
                  <label>…or metadata XML</label>
                  <textarea
                    rows={6} value={cfg.metadataXml || ''}
                    placeholder="<EntityDescriptor …>"
                    style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12, minHeight: 90 }}
                    onChange={(e) => set('metadataXml', e.target.value)}
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    Pasted XML wins over the URL. Use it when the IdP publishes no metadata endpoint.
                  </small>
                </div>

                {/* Off by default. An assertion that answers no request of ours
                    cannot prove the person meant to sign in here and now, so
                    accepting one is a deliberate choice per provider. */}
                <div className="ad-field">
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontWeight: 400 }}>
                    <input
                      type="checkbox" checked={!!cfg.allowIdpInitiated} style={{ marginTop: 3 }}
                      onChange={(e) => set('allowIdpInitiated', e.target.checked)}
                    />
                    <span>
                      <b style={{ display: 'block', marginBottom: 2 }}>
                        Allow sign-ins that start at the identity provider
                      </b>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12.5, lineHeight: 1.5 }}>
                        Needed for an application tile in Okta or Entra, where somebody clicks
                        Form-Magic and arrives already signed in. Leave it off if your people always
                        start at the Form-Magic sign-in page — an assertion sent without us asking
                        for one carries less proof that they meant to sign in here.
                        {' '}This only ever grants <b>team sign-in</b>, and only if this provider is
                        one your account has ticked for that.
                      </span>
                    </span>
                  </label>
                </div>
              </>
            )}

            {!saml && (
            <div className="fs-row-2" style={{ gap: 12 }}>
              <div className="ad-field">
                <label>Client ID</label>
                <input value={cfg.clientId || ''} onChange={(e) => set('clientId', e.target.value)} />
              </div>
              <div className="ad-field">
                <label>Client secret {cfg.clientSecretSet && !cfg.clientSecret ? '(currently set 🔒)' : ''}</label>
                <input
                  type="password"
                  placeholder={cfg.clientSecretSet ? 'Enter a new secret to replace it' : 'Leave blank for a public client (PKCE only)'}
                  value={cfg.clientSecret || ''}
                  onChange={(e) => set('clientSecret', e.target.value)}
                />
                {cfg.clientSecretSet && (
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, fontWeight: 400, fontSize: 13 }}>
                    <input
                      type="checkbox" checked={!!cfg.clientSecretCleared}
                      onChange={(e) => setCfg({ ...cfg, clientSecretCleared: e.target.checked, clientSecret: '' })}
                    />
                    Remove the stored secret (treat as a public client)
                  </label>
                )}
              </div>
            </div>
            )}

            {!saml && (
              <div className="ad-field" style={{ maxWidth: 460 }}>
                <label>Scopes</label>
                <input
                  value={Array.isArray(cfg.scopes) ? cfg.scopes.join(' ') : (cfg.scopes || '')}
                  placeholder="openid email profile"
                  onChange={(e) => set('scopes', e.target.value)}
                />
                <small style={{ color: 'var(--text-muted)' }}>Blank uses <code>openid email profile</code>.</small>
              </div>
            )}
          </div>

          {/* ---- Redirect URI ---- */}
          {provider && saml && (
            <div className="fs-card pad st-pcard">
              <h2>What to give your IdP administrator</h2>
              <p style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5, margin: '0 0 6px' }}>
                SAML registers this application through metadata rather than a redirect URI. Most identity providers can import
                the document directly from this URL:
              </p>
              <CopyUri uri={provider.callbackUri.replace(/\/callback$/, '/saml/metadata')} />
              <p style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5, margin: '10px 0 6px' }}>
                If they ask for the values by hand, the Assertion Consumer Service URL is:
              </p>
              <CopyUri uri={provider.callbackUri.replace(/\/callback$/, '/saml/callback')} />
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Ask for the assertion to be signed — this app refuses an unsigned one. Signing the response envelope as well is
                fine but not required.
              </p>
            </div>
          )}

          {provider && !saml && (
            <div className="fs-card pad st-pcard">
              <h2>Redirect URI to register</h2>
              <p style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5, margin: '0 0 6px' }}>
                People who start at a form link come back to that same form — the form is carried in the handshake, not in the
                URI, so one registration covers every form using this provider.
              </p>
              <CopyUri uri={provider.callbackUri} />
              <details style={{ fontSize: 13, color: 'var(--ink-700)' }}>
                <summary style={{ cursor: 'pointer' }}>This provider needs its own URI</summary>
                <div style={{ marginTop: 8 }}>
                  Some IdP administrators will only register a URI dedicated to one application. This provider also answers here:
                  <CopyUri uri={provider.dedicatedCallbackUri} />
                </div>
              </details>
            </div>
          )}

          {/* ---- Who this provider lets in ---- */}
          <div className="fs-card pad st-pcard">
            <h2>Who this provider lets in</h2>
            <div className="ad-field">
              <label>Allowed email domains</label>
              <DomainChips value={domains} onChange={setDomains} />
              <small style={{ color: 'var(--text-muted)' }}>
                Empty means anyone this provider authenticates. A form can narrow this further, never widen it.
              </small>
            </div>

            <div className="fb-overline" style={{ margin: '14px 0 6px' }}>Claim names</div>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.45 }}>
              These populate the pickers on a form's Access tab. A provider that advertises nothing still works — type the
              claim name your IdP contact gave you, and the first real sign-in fills in the rest.
            </p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {claimNames.map((c) => (
                <span className="sso-chip" key={c} style={{ background: 'var(--identity-bg)', color: 'var(--identity-fg)' }}>
                  {c}
                  <button type="button" onClick={() => setClaimNames(claimNames.filter((x) => x !== c))}>×</button>
                </span>
              ))}
              {!claimNames.length && <span style={{ color: 'var(--text-faint)', fontSize: 12.5 }}>None yet — run the checks, or add one by hand.</span>}
            </div>
            <div className="pv-claim-add">
              <input
                className="lg-ctl pv-claim-input"
                value={newClaim} placeholder="groups"
                onChange={(e) => setNewClaim(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  const c = newClaim.trim();
                  if (c && !claimNames.includes(c)) setClaimNames([...claimNames, c]);
                  setNewClaim('');
                }}
              />
              <button
                className="fc-act" type="button"
                onClick={() => {
                  const c = newClaim.trim();
                  if (c && !claimNames.includes(c)) setClaimNames([...claimNames, c]);
                  setNewClaim('');
                }}
              >+ Add a claim name</button>
            </div>
          </div>

          <div className="pv-save">
            <button className="ad-btn primary" disabled={saving || !name.trim()}
              onClick={() => { void save().catch(() => undefined); }}>
              {saving ? 'Saving…' : isNew ? 'Create provider' : 'Save provider'}
            </button>
            <span style={{ flex: 1 }} />
            {!isNew && (
              <button className="ad-btn danger" onClick={(e) => { e.stopPropagation(); remove(); }}>
                {confirmDelete ? 'Confirm delete…' : 'Delete'}
              </button>
            )}
          </div>
          <p className="pv-save-note" style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>
            A provider can be saved with failing checks — it just cannot become your team's sign-in until they pass.
          </p>
        </div>

        {/* ---- the checks rail ---- */}
        <div className="side">
          <div className="pe-preview" data-open={phone ? String(checksOpen) : 'true'}>
            <div className="pe-pv-head">Connection checks</div>
            {/* On a phone this rail is the first thing on the page (CSS
                `order`), so it opens as a summary line rather than as ~600px
                of detail between you and the fields. Rendered only where it is
                shown: it repeats the head's words, and a desktop should not
                have two elements saying "Connection checks". */}
            {phone && (
              <button
                type="button" className="pv-checks-toggle"
                aria-expanded={checksOpen}
                onClick={() => setChecksOpen(!checksOpen)}
              >
                Connection checks{isNew ? '' : ` — ${passing} of ${CHECK_COUNT} passing`}
                <span className="caret" aria-hidden>{checksOpen ? '▲' : '▼'}</span>
              </button>
            )}
            <div style={{ padding: 14 }}>
              {isNew ? (
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Create the provider first — the checks need somewhere to store their result.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="ad-btn primary small" disabled={checking} onClick={() => runChecks()}>
                      {checking ? 'Checking…' : 'Run checks'}
                    </button>
                    <button className="fc-act" onClick={() => setShowTestLink(!showTestLink)}>
                      Email a sign-in test link
                    </button>
                  </div>
                  <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '8px 0 12px', lineHeight: 1.5 }}>
                    No account at the provider needed — these checks never sign anyone in. Send the test link to someone who
                    does have an account when you want a real round trip confirmed.
                  </p>

                  {showTestLink && (
                    <div className="fc-note" style={{ marginBottom: 12 }}>
                      {testLinkMsg ? (
                        <div style={{ fontSize: 12.5, color: 'var(--ok-fg)' }}>{testLinkMsg}</div>
                      ) : (
                        <>
                          <div style={{ fontSize: 12.5, marginBottom: 6 }}>
                            Who should confirm it? They need an account at {name || 'this provider'} — not at Form-Magic.
                          </div>
                          <input
                            className="lg-ctl" type="email" value={testLinkEmail} placeholder="someone@t-mobile.com"
                            style={{ width: '100%', marginBottom: 6 }}
                            onChange={(e) => setTestLinkEmail(e.target.value)}
                          />
                          <button
                            className="ad-btn primary small" disabled={sendingLink || !testLinkEmail.trim()}
                            onClick={async () => {
                              setSendingLink(true); setError('');
                              try {
                                const r = await sendProviderTestLink(Number(id), testLinkEmail.trim());
                                setTestLinkMsg(`Sent to ${r.sentTo}. The link expires in ${r.expiresInMinutes} minutes — when they finish, this provider flips to “live sign-ins working”.`);
                              } catch (e: any) { setError(e.message); }
                              finally { setSendingLink(false); }
                            }}
                          >
                            {sendingLink ? 'Sending…' : 'Send the link'}
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {(resolved?.issuer || resolved?.token_endpoint) && !checking && (
                    <>
                      <div className="fb-overline" style={{ margin: '0 0 6px' }}>Resolved from discovery</div>
                      <div className="sso-resolved" style={{ marginBottom: 12 }}>
                        {([
                          ['issuer', resolved.issuer],
                          ['authorization_endpoint', resolved.authorization_endpoint],
                          ['token_endpoint', resolved.token_endpoint],
                          ['jwks_uri', resolved.jwks_uri],
                        ] as const).filter(([, v]) => v).map(([k, v]) => (
                          <div className="sso-resolved-row" key={k}>
                            <div className="k">{k}</div>
                            <div className="v">{v}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="fb-overline" style={{ margin: '0 0 4px' }}>
                    Checks{checks?.ranAt ? ` · ran ${ago(checks.ranAt)}` : ''}
                  </div>
                  <ChecksList checks={checks} running={checking} />

                  {!checking && provider?.checksClear && (
                    <div className="sso-banner ok">
                      Everything checkable without a login passes. Safe to use on a form.
                    </div>
                  )}
                  {!checking && checks && !provider?.checksClear && (
                    <div className="sso-banner bad">
                      One or more checks failed. The detail under each row names the cause and the fix.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Run checks and Save, within a thumb's reach of the bottom of a phone
          (F11). Both call the page's own handlers — there is no second code
          path for either. */}
      {phone && (
        <ActionBar className="pe-sticky">
          <button className="ad-btn primary" disabled={saving || !name.trim()}
            onClick={() => { void save().catch(() => undefined); }}>
            {saving ? 'Saving…' : isNew ? 'Create provider' : 'Save provider'}
          </button>
          {!isNew && (
            <button className="ad-btn" disabled={checking} onClick={() => { setChecksOpen(true); void runChecks(); }}>
              {checking ? 'Checking…' : 'Run checks'}
            </button>
          )}
        </ActionBar>
      )}
      {guard.node}
    </div>
  );
}
